import React from 'react';

import { Box, Divider } from '@mui/material';
import cn from 'clsx';
import LoadingSpinner from 'components/LoadingSpinner';
import Tooltip from 'components/Tooltip';
import Button from 'components/V2/Button';
import PropTypes from 'prop-types';

import styles from './formComponents.module.css';

// * btnText -> passed to primary action button
// * formType -> two values can be passed. default is submit 👇
// * 1) "onClick" = when primary button need custom onClick function.
// * 2) "submit" = when form type is submit no custom event handler needed
// * secondaryBtnHandler -> to handle onClick functionality of secondary button (Cancel button)
// * primaryBtnHandler -> only required when form type is not submit

function FormFooter({
    btnText,
    secondaryBtnText,
    secondaryBtnHandler,
    primaryBtnHandler,
    formType = 'submit',
    form,
    isPrimaryBtnDisabled = false,
    isSecondaryBtnDisabled = false,
    primaryClassName,
    secondaryClassName,
    primaryBtnLoading = false,
    secondaryBtnLoading = false,
    primaryBtnTooltipText,
    children,
    isDanger = false,
    btnContainerClassName,
    btnRowClassName,
    hideDivider = false,
}) {
    // Danger keeps its own intent while disabled -- the Button owns that state.
    // Everything else still greys out by switching to secondary.
    let primaryBtnIntent = 'primary';

    if (isDanger) primaryBtnIntent = 'danger';
    else if (isPrimaryBtnDisabled) primaryBtnIntent = 'secondary';
    else primaryBtnIntent = 'primary';

    return (
        <>
            {!hideDivider && <Divider className={styles.divider} />}
            <Box className={cn(styles.commonFormFooter, btnContainerClassName)}>
                {children}
                <Box className={cn(styles.footerBtnContainer, btnRowClassName)}>
                    <Button
                        intent="secondary"
                        className={secondaryClassName}
                        onClick={secondaryBtnHandler}
                        disabled={isSecondaryBtnDisabled || secondaryBtnLoading}
                        endIcon={
                            secondaryBtnLoading && (
                                <LoadingSpinner
                                    text={null}
                                    spinnerClassName={styles.primaryBtnSpinner}
                                />
                            )
                        }
                    >
                        {secondaryBtnText || 'Cancel'}
                    </Button>
                    <Tooltip title={primaryBtnTooltipText || ''} arrow placement="top-end">
                        <span>
                            <Button
                                intent={primaryBtnIntent}
                                className={primaryClassName}
                                onClick={formType === 'onClick' ? primaryBtnHandler : undefined}
                                type={formType === 'onClick' ? 'button' : 'submit'}
                                form={form}
                                disabled={isPrimaryBtnDisabled || primaryBtnLoading}
                                endIcon={
                                    primaryBtnLoading && (
                                        <LoadingSpinner
                                            text={null}
                                            spinnerClassName={styles.primaryBtnSpinner}
                                        />
                                    )
                                }
                            >
                                {btnText}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </>
    );
}

FormFooter.propTypes = {
    btnText: PropTypes.string.isRequired,
    secondaryBtnText: PropTypes.string,
    formType: PropTypes.oneOf(['onClick', 'submit']),
    form: PropTypes.string,
    primaryBtnHandler: PropTypes.func,
    secondaryBtnHandler: PropTypes.func.isRequired,
    isPrimaryBtnDisabled: PropTypes.bool,
    isSecondaryBtnDisabled: PropTypes.bool,
    primaryClassName: PropTypes.string,
    secondaryClassName: PropTypes.string,
    primaryBtnTooltipText: PropTypes.string,
    children: PropTypes.node,
    isDanger: PropTypes.bool,
    btnContainerClassName: PropTypes.string,
    btnRowClassName: PropTypes.string,
    primaryBtnLoading: PropTypes.bool,
    secondaryBtnLoading: PropTypes.bool,
    hideDivider: PropTypes.bool,
};

export default FormFooter;
